import React, { useState, useEffect } from 'react';
import { Trash } from 'iconoir-react';
import { getUserCourses, deleteCourse } from '../context/engine.ts';
import { supabase } from '../integrations/client.ts';

function CourseHistory({ courseHistoryState, onClose, currentUser, isAuthenticated, profileLoaded = false, onCourseSelect, currentCourse, onLoadingComplete, onNavigateToLanding }) {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null); // { courseId, courseName }

    // Fetch user courses when component mounts or user changes
    // IMPORTANT: Wait for profileLoaded before fetching to ensure subscription_type is available
    useEffect(() => {
        const fetchCourses = async () => {
            // Don't fetch if profile hasn't loaded yet
            if (!profileLoaded) {
                return;
            }

            if (!isAuthenticated || !currentUser) {
                setCourses([]);
                setLoading(false);
                // Signal completion even when not authenticated
                if (onLoadingComplete) {
                    onLoadingComplete();
                }
                return;
            }

            if (!currentUser.id) {
                setError('User ID not available. Please try signing in again.');
                setLoading(false);
                // Signal completion even on error
                if (onLoadingComplete) {
                    onLoadingComplete();
                }
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const userCourses = await getUserCourses(currentUser.id);
                setCourses(userCourses);
                setError(null);
            } catch (err) {
                setError(err.message || 'Failed to load courses');
                setCourses([]);
            } finally {
                setLoading(false);
                // Signal that course history has finished loading
                if (onLoadingComplete) {
                    onLoadingComplete();
                }
            }
        };

        fetchCourses();
    }, [isAuthenticated, currentUser, profileLoaded, onLoadingComplete]);

    if (courseHistoryState === 'hidden') return null;
    const currentTier = String((currentUser?.subscriptionStatus || currentUser?.subscriptionType || currentUser?.subscription_type || 'free')).toLowerCase();
    
    const handleUpgrade = async () => {
        try {
            const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
            if (sessionError || !sessionData.session) throw new Error('Not authenticated');

            // Fetch premium price from subscription_tiers
            const { data: premiumTier, error: tierError } = await supabase
                .from('subscription_tiers')
                .select('stripe_price_id')
                .eq('id', 'premium')
                .single();

            if (tierError || !premiumTier?.stripe_price_id) {
                throw new Error('Premium plan is not configured. Please contact support.');
            }

            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const res = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionData.session.access_token}`
                },
                body: JSON.stringify({
                    priceId: premiumTier.stripe_price_id,
                    successUrl: `${window.location.origin}/?upgrade=success`,
                    cancelUrl: `${window.location.origin}/?upgrade=cancel`
                })
            });

            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Failed to create checkout session');

            if (json.url) {
                window.location.href = json.url;
            } else {
                throw new Error('Checkout URL not returned');
            }
        } catch (err) {
            alert(err.message || 'Unable to start upgrade checkout');
        }
    };

    const handleCourseClick = (course) => {
        if (onCourseSelect) {
            onCourseSelect(course);
        }
    };

    const handleDeleteClick = (e, course) => {
        e.stopPropagation(); // Prevent course selection when clicking delete
        setDeleteConfirm({ courseId: course.id, courseName: course.name });
    };

    const handleConfirmDelete = async () => {
        if (!deleteConfirm) return;

        try {
            await deleteCourse(deleteConfirm.courseId);
            setCourses(courses.filter(c => c.id !== deleteConfirm.courseId));
            setDeleteConfirm(null);
            
            // If the deleted course was the current course, clear selection
            if (currentCourse?.id === deleteConfirm.courseId && onCourseSelect) {
                onCourseSelect(null);
            }
        } catch (err) {
            setError(err.message || 'Failed to delete course');
            setDeleteConfirm(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirm(null);
    };
    
    return (
        <div className={`course-history-container ${courseHistoryState}`}>
            <div className="course-history-panel">
                <div className="course-history-header">
                    <h2>Course History</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>
                {isAuthenticated && currentUser && (
                    <div style={{
                        padding: '12px 24px',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px'
                    }}>
                        {currentTier === 'premium' ? (
                            <div style={{ fontSize: '14px', color: '#333', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>Current plan:</span>
                                <span className="premium-badge">Premium</span>
                            </div>
                        ) : null}
                        {currentTier === 'free' && (
                            <div style={{ fontSize: '14px', color: '#333', lineHeight: '1.4' }}>
                                <a 
                                    href="#upgrade"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleUpgrade();
                                    }} 
                                    style={{
                                        color: '#1976d2',
                                        textDecoration: 'none',
                                        cursor: 'pointer',
                                        transition: 'color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.color = '#1565c0';
                                        e.currentTarget.style.textDecoration = 'underline';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.color = '#1976d2';
                                        e.currentTarget.style.textDecoration = 'none';
                                    }}
                                >
                                    Upgrade to Premium
                                </a>
                                {' to generate a crash course on almost any topic you want!'}
                            </div>
                        )}
                    </div>
                )}
                <div className="course-history-content">
                    {!isAuthenticated ? (
                        <div className="no-history-message" style={{
                            textAlign: 'center',
                            color: '#666',
                            padding: '20px',
                            fontStyle: 'italic'
                        }}>
                            Please sign in to view your course history
                        </div>
                    ) : loading ? (
                        <div style={{ padding: '20px' }}>
                            {/* Skeleton loading for course cards */}
                            {[1, 2, 3].map((i) => (
                                <div key={i} style={{
                                    marginBottom: '12px',
                                    padding: '16px',
                                    borderRadius: '8px',
                                    backgroundColor: '#f5f5f5'
                                }}>
                                    <div style={{
                                        width: '70%',
                                        height: '20px',
                                        backgroundColor: '#e0e0e0',
                                        borderRadius: '4px',
                                        marginBottom: '8px',
                                        animation: 'pulse 1.5s ease-in-out infinite',
                                        animationDelay: `${i * 0.1}s`
                                    }} />
                                    <div style={{
                                        width: '40%',
                                        height: '14px',
                                        backgroundColor: '#e0e0e0',
                                        borderRadius: '4px',
                                        animation: 'pulse 1.5s ease-in-out infinite',
                                        animationDelay: `${i * 0.1 + 0.1}s`
                                    }} />
                                </div>
                            ))}
                        </div>
                    ) : error ? (
                        <div className="error-message" style={{
                            textAlign: 'center',
                            padding: '20px'
                        }}>
                            <div style={{
                                color: '#e74c3c',
                                marginBottom: '12px',
                                fontStyle: 'italic'
                            }}>
                                {error}
                            </div>
                            <button
                                onClick={() => {
                                    setError(null);
                                    if (isAuthenticated && currentUser?.id) {
                                        setLoading(true);
                                        getUserCourses(currentUser.id)
                                            .then(userCourses => {
                                                setCourses(userCourses);
                                                setError(null);
                                            })
                                            .catch(err => {
                                                setError(err.message || 'Failed to load courses');
                                                setCourses([]);
                                            })
                                            .finally(() => setLoading(false));
                                    }
                                }}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: '1px solid #e74c3c',
                                    backgroundColor: 'white',
                                    color: '#e74c3c',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#e74c3c';
                                    e.currentTarget.style.color = 'white';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'white';
                                    e.currentTarget.style.color = '#e74c3c';
                                }}
                            >
                                Retry
                            </button>
                        </div>
                    ) : courses.length === 0 ? (
                        <div className="no-history-message" style={{
                            textAlign: 'center',
                            color: '#666',
                            padding: '20px',
                            fontStyle: 'italic'
                        }}>
                            {currentTier === 'premium' 
                                ? 'All asked subjects will appear here. Start by asking the Orb about a subject.'
                                : 'Your course history will appear here. Start by selecting a course from the library.'}
                        </div>
                    ) : (
                        courses.map((course, index) => {
                            const isTemplateDeleted = course.template_deleted === true;
                            const isDisabled = isTemplateDeleted;

                            return (
                                <div
                                    key={course.id}
                                    className={`course-item ${currentCourse?.id === course.id ? 'selected' : ''}`}
                                    onClick={() => !isDisabled && handleCourseClick(course)}
                                    style={{
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        padding: '10px 10px 10px 24px',
                                        borderRadius: '5px',
                                        margin: '5px 0',
                                        backgroundColor: currentCourse?.id === course.id ? '#e3f2fd' : 'transparent',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        opacity: isDisabled ? 0.6 : 1,
                                        position: 'relative'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isDisabled && currentCourse?.id !== course.id) {
                                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isDisabled && currentCourse?.id !== course.id) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                    }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <span style={{
                                            textDecoration: isTemplateDeleted ? 'line-through' : 'none',
                                            color: isTemplateDeleted ? '#999' : 'inherit'
                                        }}>
                                            {course.name.charAt(0).toUpperCase() + course.name.slice(1)}
                                        </span>
                                        {isTemplateDeleted && (
                                            <span style={{
                                                fontSize: '11px',
                                                color: '#e74c3c',
                                                marginTop: '2px',
                                                fontStyle: 'italic'
                                            }}>
                                                Template no longer available
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        onClick={(e) => handleDeleteClick(e, course)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '4px 8px',
                                            color: '#999',
                                            fontSize: '16px',
                                            transition: 'color 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.color = '#e74c3c';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.color = '#999';
                                        }}
                                        title={isTemplateDeleted ? "Delete unavailable course" : "Delete course"}
                                    >
                                        <Trash width={16} height={16} strokeWidth={2} />
                                    </button>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer with Back to Home button - only show for premium users on Inquiry page */}
                {onNavigateToLanding && currentTier === 'premium' && (
                    <div className="course-history-footer" style={{
                        padding: '16px 24px',
                        backgroundColor: 'transparent'
                    }}>
                        <button
                            onClick={() => {
                                onClose();
                                setTimeout(() => onNavigateToLanding(), 300);
                            }}
                            style={{
                                width: '100%',
                                padding: '12px 16px',
                                backgroundColor: '#333333',
                                color: '#f8f9fa',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#444444';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#333333';
                            }}
                        >
                            <span>←</span>
                            <span>Back to Home</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '24px',
                        borderRadius: '8px',
                        maxWidth: '400px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}>
                        <h3 style={{ marginTop: 0, marginBottom: '12px', color: '#333' }}>
                            Delete Course
                        </h3>
                        <p style={{ marginBottom: '20px', color: '#666' }}>
                            Are you sure you want to delete "<strong>{deleteConfirm.courseName.charAt(0).toUpperCase() + deleteConfirm.courseName.slice(1)}</strong>"? 
                            This action cannot be undone.
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={handleCancelDelete}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd',
                                    backgroundColor: 'white',
                                    cursor: 'pointer',
                                    color: '#333',
                                    transition: 'background-color 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'white';
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    backgroundColor: '#e74c3c',
                                    color: 'white',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#c0392b';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#e74c3c';
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CourseHistory;